import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { cls } from '@/shared/lib/classes';

/**
 * A dica de um controle: a marca `?` ao lado do rótulo e a nota que ela abre.
 *
 * ## Por que existe fora do `Campo`
 *
 * A marca nasceu no `Campo`, e a caixa de seleção precisou da mesma coisa: a
 * `descricao` dela ("Pode figurar em contratos e contas a receber.") ocupava uma
 * linha sob cada caixa, e três caixas empilhadas com explicação embaixo eram
 * meia tela para dizer o que se lê uma vez na vida. Duplicar o botão nos dois
 * componentes faria os dois divergirem na primeira mudança de cor — e a marca é
 * justamente o que precisa ser reconhecível como *a mesma coisa* em qualquer
 * lugar da tela.
 *
 * ## Por que hook, e não componente
 *
 * As duas peças não moram juntas: a marca vai **na linha do rótulo** e a nota
 * vai **abaixo do controle**, com o controle entre elas. Um componente único
 * teria de receber o controle como filho e montar a estrutura inteira, e aí
 * `Campo` e `Caixa` — que têm estruturas diferentes de propósito, uma com
 * `<label htmlFor>` e outra com o `<input>` dentro do rótulo — passariam a
 * disputar a mesma moldura. O hook devolve as duas peças prontas e deixa cada um
 * pendurá-las onde lhe cabe.
 *
 * ## O que não muda
 *
 * O texto **continua no DOM mesmo fechado**, como `sr-only`, e o `idDica` vai no
 * `aria-describedby` do controle. Renderizar só quando aberto é o caminho óbvio
 * e tira a explicação de quem usa leitor de tela — que é justamente quem não tem
 * como "olhar em volta" para deduzir o que a caixa faz. O botão é conveniência
 * visual; a informação nunca sai da árvore de acessibilidade.
 *
 * E o exemplo nunca volta para dentro do controle como `placeholder`: texto
 * cinza na moldura se lê como valor preenchido e some na primeira tecla. Essa
 * decisão não mudou — o que mudou é que a explicação deixou de ficar aberta o
 * tempo todo, cobrando uma linha de tela de quem já a leu.
 *
 * ## Por que a dica deixou de ser só `string`
 *
 * Ela nasceu `string`, e nos cadastros basta: "Ex.: 2026/014-ADM" não tem
 * ênfase nem data. Os painéis trouxeram o outro caso — "O valor é sempre o
 * **saldo**, e não o valor original: o de R$ 1.000,00 com R$ 700,00 pagos entra
 * por R$ 300,00" é uma frase em que o negrito é o que se lê primeiro, e várias
 * dessas notas citam a data que a consulta devolveu. Achatá-las em texto puro
 * custaria a ênfase; montá-las por concatenação de template custaria a data
 * formatada no meio de uma frase, que é onde o erro de espaço em branco mora.
 *
 * O preço é o `title`: o atributo do HTML só aceita texto, e por isso ele **só
 * existe quando a dica é `string`**. Perdê-lo no texto rico é aceitável — o
 * `title` é a segunda porta, para quem não descobriu que a marca é clicável, e
 * nas notas de painel a marca fica ao lado de um título de bloco, onde o clique
 * é o gesto óbvio. Nos campos de formulário, que são a maioria, ele continua.
 */

export type TomDaDica = 'claro' | 'escuro';

/** Fechada: contorno fino, como uma chamada de nota ainda não lida. */
const COR_MARCA = {
  claro: 'border-grafite-300 text-grafite-500 hover:border-grafite-900 hover:text-grafite-900',
  escuro: 'border-grafite-600 text-grafite-400 hover:border-papel-300 hover:text-papel-100',
} as const;

/** Aberta, é preenchida: é o que liga a marca ao texto que apareceu abaixo. */
const COR_MARCA_ABERTA = {
  claro: 'border-zarcao-600 bg-zarcao-600 text-papel-50',
  escuro: 'border-zarcao-500 bg-zarcao-500 text-papel-50',
} as const;

/** A nota aberta, com o filete na lateral — anotação na margem da prancha. */
const COR_NOTA = {
  claro: 'border-l-2 border-zarcao-500 bg-papel-100 text-grafite-600',
  escuro: 'border-l-2 border-zarcao-500 bg-grafite-800 text-grafite-300',
} as const;

export interface PecasDaDica {
  /**
   * Vai no `aria-describedby` do controle. `undefined` quando não há dica —
   * apontar para um elemento que não existe faz o leitor de tela anunciar nada
   * onde deveria anunciar a explicação.
   */
  idDica: string | undefined;
  /** O `?`, para a linha do rótulo. `null` quando não há dica. */
  marca: ReactElement | null;
  /** O texto, para depois do controle. `null` quando não há dica. */
  nota: ReactElement | null;
}

export function useDica({
  dica,
  rotulo,
  alvo = 'campo',
  tom = 'claro',
}: {
  /**
   * `string` no campo de formulário; `ReactNode` nas notas de painel, que têm
   * ênfase e data interpolada. Só a `string` vira `title` — ver o cabeçalho.
   */
  dica: ReactNode;
  /** Entra no rótulo do botão: "Mostrar a explicação do campo Nome fantasia". */
  rotulo: string;
  /**
   * O substantivo do rótulo do botão. `campo` no `Campo` e na `Caixa`.
   *
   * Existe porque a marca saiu dos controles: o subtítulo de um bloco também a
   * usa, e ali "a explicação do campo Grupos de insumo oferecidos" descreve como
   * campo o que é uma lista inteira — quem enxerga a tela corrige sozinho, quem
   * ouve não tem como. `bloco` é o valor que esse caso usa.
   */
  alvo?: string;
  tom?: TomDaDica;
}): PecasDaDica {
  const id = useId();
  const [aberta, setAberta] = useState(false);

  if (!dica) return { idDica: undefined, marca: null, nota: null };

  const marca = (
    <button
      type="button"
      onClick={() => setAberta((antes) => !antes)}
      /*
        Fora da ordem do Tab de propósito. Preencher um cadastro é uma corrida de
        Tab entre campos, e a marca se intercala entre cada dois deles: quem digita
        pagava uma parada por campo com dica para chegar ao próximo. Tirar o botão
        do Tab não esconde nada de quem usa leitor de tela — o texto está sempre no
        DOM e chega pelo `aria-describedby` do controle, junto com o rótulo, sem
        depender de encontrar o botão. O botão é atalho visual para o mouse.

        A alternativa era manter o foco e abrir a nota no `focus`; foi recusada
        porque a explicação passaria a saltar em toda passagem de Tab, exatamente
        o barulho que a marca recolhível veio remover.
      */
      tabIndex={-1}
      aria-expanded={aberta}
      aria-controls={id}
      aria-label={`${aberta ? 'Ocultar' : 'Mostrar'} a explicação do ${alvo} ${rotulo}`}
      // O `title` repete a dica no repouso do mouse: quem não descobriu que a
      // marca é clicável ainda assim lê o texto. Só existe no texto puro — o
      // atributo do HTML não aceita marcação, e serializar a árvore aqui daria
      // uma frase sem os espaços que o JSX guarda entre os elementos.
      title={typeof dica === 'string' ? dica : undefined}
      className={cls(
        'relative grid h-4 w-4 shrink-0 place-items-center border font-mono text-[10px] font-semibold leading-none transition-colors',
        /*
          Os 16px do quadrado são de desenho, não de alvo: o `after` invisível
          estende o clique para 24px sem empurrar o rótulo. Ele para em 24px de
          propósito — os 48px de obra passariam por cima da borda do controle
          logo abaixo e roubariam o clique de quem só queria focar o campo.
        */
        "after:absolute after:-inset-1 after:content-['']",
        aberta ? COR_MARCA_ABERTA[tom] : COR_MARCA[tom],
      )}
    >
      ?
    </button>
  );

  /*
    `<div>` e não `<p>`: as notas de painel têm dois parágrafos, e um `<p>`
    dentro de outro é fechado pelo navegador antes do primeiro filho — o
    resultado é a segunda metade da explicação escapando da moldura amarela,
    sem nada no console avisando. Para a dica de uma linha só, que é a maioria,
    o elemento não muda nada: quem a anuncia é o `aria-describedby`.
  */
  const nota = (
    <div id={id} className={aberta ? cls('px-2 py-1 text-xs', COR_NOTA[tom]) : 'sr-only'}>
      {dica}
    </div>
  );

  return { idDica: id, marca, nota };
}
